/*
 * Política de recompensas de cada invasión según la información APROBADA en la guía
 * (data/invasions.ts) y las indicaciones de la administración.
 *
 * Una ruta exportada que relaciona un ítem con una invasión solo se muestra a los jugadores
 * si el ítem está autorizado de forma explícita. Una descripción general ("armas y sets",
 * "joyas del reparto general") NO basta para atribuir cualquier ítem al evento: esos casos
 * quedan retenidos hasta recibir la lista vigente. Nada se borra de los archivos originales.
 */

export type ItemCategory = 'weapon' | 'set' | 'jewel' | 'other'

export type InvasionPolicy = {
  guideEntryId: string
  name: string
  /** event_id comprobados (ver eventEquivalences.ts). */
  eventIds: string[]
  /** monster_id y bag_id confirmados por el staff para esta invasión. */
  monsterIds: number[]
  bagIds: string[]
  /** Texto aprobado que se contrasta. */
  approved: string
  /** Nombres exactos autorizados (nombre del juego). */
  authorizedNames: string[]
  /** item_id base autorizados por ID exacto (p. ej. 14,11 = cajas Kundun, cuyas variantes comparten base). */
  authorizedItemIds?: string[]
  /** Categorías que la información aprobada menciona de forma general, sin lista exacta. */
  generalCategories: ItemCategory[]
  /** La información aprobada excluye ítems Excellent / Ancient. */
  forbidExcellent: boolean
  /** Solo ítems Excellent dentro de las categorías generales (p. ej. el 20 % Excellent del White Wizard). */
  excellentOnlyGeneral?: boolean
  /** Niveles +N aprobados. Vacío = sin restricción documentada. */
  levels?: number[]
  /** Qué dato concreto falta para validar las categorías generales. */
  missing?: string
}

export const invasionPolicies: InvasionPolicy[] = [
  {
    guideEntryId: 'death-king',
    name: 'Death King / Skeleton',
    eventIds: ['invasion:0'],
    monsterIds: [],
    bagIds: [],
    approved: 'Jefe: 2 joyas del reparto general. Cada acompañante: 1 pieza de set o arma tier 1–4, de +6 a +9, NO Excellent.',
    authorizedNames: [],
    generalCategories: ['weapon', 'set', 'jewel'],
    forbidExcellent: true,
    levels: [6, 7, 8, 9],
    missing: 'Lista vigente de ítems (item_id + nivel) de los tiers 1–4 y lista de joyas del "reparto general".',
  },
  {
    guideEntryId: 'red-dragon',
    name: 'Red Dragon',
    eventIds: ['invasion:1'],
    monsterIds: [42],
    bagIds: ['2'],
    approved: 'Cada dragón: 2 joyas del reparto general y 2 piezas de set o armas tier 4–7, nivel +9 o +10, NO Excellent.',
    authorizedNames: [],
    generalCategories: ['weapon', 'set', 'jewel'],
    forbidExcellent: true,
    levels: [9, 10],
    missing: 'Lista vigente de ítems (item_id + nivel) de los tiers 4–7 y lista de joyas del "reparto general". La exportación de bag 2 (146 rutas / 145 ítems) no coincide con el drop vigente.',
  },
  {
    guideEntryId: 'white-wizard',
    name: 'White Wizard',
    eventIds: ['invasion:3'],
    monsterIds: [],
    bagIds: [],
    approved: 'Jefe: 3 joyas del reparto general y 20 % de un ítem Excellent adicional. Acompañantes: 20 % Wizard’s Ring, 80 % 75.000 Zen.',
    authorizedNames: ["Wizard's Ring"],
    generalCategories: ['weapon', 'set', 'jewel'],
    forbidExcellent: false,
    excellentOnlyGeneral: true,
    missing: 'Lista vigente de ítems Excellent posibles del jefe y lista de joyas del "reparto general".',
  },
  {
    guideEntryId: 'new-year',
    name: 'New Year',
    eventIds: ['invasion:4'],
    monsterIds: [],
    bagIds: [],
    approved: 'Cada monstruo: 1 joya garantizada (70 % Jewel of Chaos, 30 % Jewel of Soul).',
    authorizedNames: ['Jewel of Chaos', 'Jewel of Soul'],
    generalCategories: [],
    forbidExcellent: true,
  },
  {
    guideEntryId: 'rabbits',
    name: 'Rabbits',
    eventIds: ['invasion:5'],
    monsterIds: [],
    bagIds: [],
    approved: 'Cada monstruo: 1 joya garantizada (70 % Jewel of Chaos, 30 % Jewel of Bless).',
    authorizedNames: ['Jewel of Chaos', 'Jewel of Bless'],
    generalCategories: [],
    forbidExcellent: true,
  },
  {
    guideEntryId: 'summer',
    name: 'Summer',
    eventIds: ['invasion:6'],
    monsterIds: [],
    bagIds: [],
    approved: 'Cada monstruo: 1 joya garantizada (50 % Jewel of Life, 50 % Jewel of Creation).',
    authorizedNames: ['Jewel of Life', 'Jewel of Creation'],
    generalCategories: [],
    forbidExcellent: true,
  },
  {
    // Ficha editorial publicada con autorización del staff (configuración del 08/10/2026, no probada en juego).
    guideEntryId: 'golden',
    name: 'Dorados (invasion:2)',
    eventIds: ['invasion:2'],
    monsterIds: [43, 78, 53, 54, 79, 80, 81, 82, 83, 502, 493, 494, 495, 496, 497, 498, 500, 499, 501],
    bagIds: [],
    approved: 'Cada dorado entrega cajas Kundun (+1 a +5 según el monstruo). El equipo sale al abrir esas cajas: es una recompensa indirecta.',
    authorizedNames: ['Box of Kundun 1', 'Box of Kundun 2', 'Box of Kundun 3', 'Box of Kundun 4', 'Box of Kundun 5'],
    authorizedItemIds: ['14,11'],
    generalCategories: [],
    forbidExcellent: false,
    missing: 'Los dorados solo entregan cajas Kundun; cualquier otro ítem directo no figura en la ficha publicada.',
  },
]

/** Asociaciones contradichas expresamente por la administración. */
export const adminConflicts: { guideEntryId: string; itemId: string; note: string }[] = [
  { guideEntryId: 'red-dragon', itemId: '7,1', note: 'La administración indica que Dragon Helm no forma parte del drop vigente de Red Dragon.' },
]
